import { AreaChart, type AreaData, useChartTheme } from '@revelio/replots';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { usePlotMetric } from '../../hooks/usePlotMetric';
import type { DistributionDataSet } from '../DataProvider/types';
import { useChart } from './ChartProvider';
import { SharableChart } from './chart-menu/plot-share/SharableChart';
import { getFormat } from './utils/getFormat';

const isValidPoint = (
  v: { value: number; kde: number; cdf: number } | null | undefined
): v is { value: number; kde: number; cdf: number } =>
  v != null &&
  typeof v.value === 'number' &&
  typeof v.kde === 'number' &&
  typeof v.cdf === 'number' &&
  // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
  !isNaN(v.value) &&
  // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
  !isNaN(v.kde) &&
  // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
  !isNaN(v.cdf);

function getCutoffBounds(
  data: DistributionDataSet['data'],
  lowerPct: number,
  upperPct: number
): { lowerX: number; upperX: number } {
  const validData = data.filter(
    (d) => d && d.dim && d.distribution && d.distribution.length > 0
  );
  if (validData.length === 0) {
    return { lowerX: 0, upperX: 0 };
  }
  const allValues = validData.flatMap((d) =>
    d.distribution.filter(isValidPoint).map((v) => v.value)
  );
  const globalMin = Math.min(...allValues);
  const globalMax = Math.max(...allValues);
  const lowerF = lowerPct / 100;
  const upperF = upperPct / 100;

  let lowerX = Number.POSITIVE_INFINITY;
  let upperX = Number.NEGATIVE_INFINITY;

  for (const d of validData) {
    const pts = d.distribution.filter(isValidPoint);
    if (pts.length === 0) continue;
    if (lowerPct > 0) {
      const withCdfAtOrBelow = pts.filter((v) => v.cdf <= lowerF);
      const firstPt = pts[0];
      const x = (() => {
        if (withCdfAtOrBelow.length > 0) {
          return Math.max(...withCdfAtOrBelow.map((v) => v.value));
        }
        if (firstPt !== undefined) return firstPt.value;
        return 0;
      })();
      lowerX = Math.min(lowerX, x);
    }
    if (upperPct < 100) {
      const firstAtOrAbove = pts.find((v) => v.cdf >= upperF);
      const lastPt = pts[pts.length - 1];
      const x = (() => {
        if (firstAtOrAbove !== undefined) return firstAtOrAbove.value;
        if (lastPt !== undefined) return lastPt.value;
        return 0;
      })();
      upperX = Math.max(upperX, x);
    }
  }

  return {
    lowerX: lowerPct <= 0 ? globalMin : lowerX,
    upperX: upperPct >= 100 ? globalMax : upperX,
  };
}

function applyCutoff(
  data: DistributionDataSet['data'],
  lowerX: number,
  upperX: number
): DistributionDataSet['data'] {
  return data.map((d) => {
    if (!d?.distribution?.length) return d;
    const filtered = d.distribution.filter(
      (v) => isValidPoint(v) && v.value >= lowerX && v.value <= upperX
    );
    return { ...d, distribution: filtered };
  });
}

const convertDistributionToAreaData = (
  data: DistributionDataSet['data']
): AreaData[] =>
  data
    .filter((d) => d && d.dim && d.distribution && d.distribution.length > 0)
    .map((d) => ({
      label: d.dim,
      values: d.distribution.filter(isValidPoint).map((v) => ({
        x: v.value,
        value: v.kde,
        secondaryValue: v.cdf,
      })),
      ...(d.percentiles && { percentiles: d.percentiles }),
    }))
    .filter((d) => d.values.length > 0);

interface DistributionProps {
  data: DistributionDataSet['data'];
  colorOverride?: string;
}

export const Distribution = ({ data, colorOverride }: DistributionProps) => {
  const { item } = useChart();
  const metric = usePlotMetric();
  const { code } = useCurrencyPreference();
  const { palette } = useChartTheme();

  const rawLower = item?.plot?.distributionCutoffLower ?? 1;
  const rawUpper = item?.plot?.distributionCutoffUpper ?? 99;
  let lowerPct = Math.min(100, Math.max(0, rawLower));
  const upperPct = Math.min(100, Math.max(0, rawUpper));
  if (lowerPct >= upperPct) {
    lowerPct = Math.max(0, upperPct - 1);
  }
  const { lowerX, upperX } = getCutoffBounds(data, lowerPct, upperPct);
  const trimmedData = applyCutoff(data, lowerX, upperX);
  const areaData: AreaData[] = convertDistributionToAreaData(trimmedData);
  const colorPalette = item?.plot?.colorPalette ?? palette;
  const colors = areaData
    .map((_, i) => colorOverride || colorPalette[i % colorPalette.length])
    .filter((color) => color !== undefined);

  const format = item?.plot?.format ?? getFormat({ metric });
  const showQuartiles = item?.plot?.showQuartiles ?? false;

  return (
    <SharableChart
      Component={AreaChart}
      data={areaData}
      format={format}
      currencyCode={code}
      colors={colors}
      showYAxis={false}
      showQuartiles={showQuartiles}
    />
  );
};
